import { mapKeys, snakeCase } from 'lodash';
import * as R from 'ramda';

import { isoDateOnlyFormat, toDate } from 'App/services/DateService';
import convertKeysToCamelCase from 'App/utils/keyConversion/convertKeysToCamelCase';
import dayjs from 'Shared/dayjs';
import { setPreference } from 'App/contexts/UserPreferences.utils';

export const processWorkQueueResult = (records) => {
    return records?.map((record) => ({
        ...convertKeysToCamelCase(record),
        patientName: `${record.patientLastName}, ${record.patientFirstName}`,
        highRisk: record.high_risk === '1',
    }));
};

/**
 * The dates used in the filter expect the YYYY-MM-DD format. And when the search is performed on the back-end, it will
 * concatenate that value with time values of "00:00:00" nad "23:59:59", respectively.
 * @param date
 * @returns {*}
 */
export const workQueueFormatDate = (date) => {
    if (date) {
        return dayjs(date).format(isoDateOnlyFormat);
    }
    return date;
};

export const setWorkQueueFilterGenerator =
    (setFieldValue) =>
    (currentState, filterName, filterValue, clearActiveSavedFilter = false) => {
        const updatedFilter = {
            ...currentState,
            [filterName]: filterValue,
            page: 1,
        };
        setFieldValue('filterParams', updatedFilter);
        if (clearActiveSavedFilter) {
            setFieldValue('activeSavedFilter', null);
            setPreference('workQueue_activeSavedFilter', null).catch(() => {});
            setPreference('assignmentManager_activeSavedFilter', null).catch(() => {});
        }
    };

export const setWorkQueuePendingFilterGenerator =
    (setFieldValue) =>
    (currentState, filterName, filterValue, clearActiveSavedFilter = false) => {
        const updatedFilter = {
            ...currentState,
            [filterName]: filterValue,
        };
        setFieldValue('pendingFilterParams', updatedFilter);
        if (clearActiveSavedFilter) {
            setFieldValue('activeSavedFilter', null);
            setPreference('workQueue_activeSavedFilter', null).catch(() => {});
            setPreference('assignmentManager_activeSavedFilter', null).catch(() => {});
        }
    };

const convertSearchFilter = (search) => mapKeys(search, (value, key) => snakeCase(key));

const normalizeBranchesForDb = (input = {}, allBranchesList = []) => {
    const selectedBranches = input?.branches || [];
    const availableBranchIds = (allBranchesList || []).map((branch) => branch.id).filter(Boolean);

    if (!input?.excludeBranches || !availableBranchIds.length) {
        return {
            branches: selectedBranches,
            allBranches: input?.allBranches,
        };
    }

    const selectedSet = new Set(selectedBranches);
    const includedBranches = availableBranchIds.filter((id) => !selectedSet.has(id));

    return {
        branches: includedBranches,
        allBranches: includedBranches.length === availableBranchIds.length,
    };
};

const normalizeCategoriesForDb = (input = {}, allCategoriesList = []) => {
    const selectedCategories = input?.categories || [];
    const availableCategoryIds = (allCategoriesList || []).map((category) => category.id).filter(Boolean);

    if (!input?.excludeCategories || !availableCategoryIds.length) {
        return {
            categories: selectedCategories,
            allCategories: input?.allCategories,
        };
    }

    const selectedSet = new Set(selectedCategories);
    const includedCategories = availableCategoryIds.filter((id) => !selectedSet.has(id));

    return {
        categories: includedCategories,
        allCategories: includedCategories.length === availableCategoryIds.length,
    };
};

const normalizeActivityTypesForDb = (input = {}, allActivityTypesList = [], allCustomOptionsList = []) => {
    const selectedFilters = input?.filters || [];
    const availableFilterIds = (allActivityTypesList || []).map((activityType) => activityType.id).filter(Boolean);
    const customOptionIds = (allCustomOptionsList || []).map((option) => option.id).filter(Boolean);

    if (!input?.excludeActivityTypes || (!availableFilterIds.length && !customOptionIds.length)) {
        return {
            filters: selectedFilters,
            allTypes: input?.allTypes,
        };
    }

    const selectedSet = new Set(selectedFilters);
    const includedFilters = availableFilterIds.filter((id) => !selectedSet.has(id));

    // Invert the custom option booleans: selected options become false, unselected become true.
    const invertedCustomOptions = customOptionIds.reduce((acc, id) => {
        acc[id] = !input?.[id];
        return acc;
    }, {});

    return {
        filters: includedFilters,
        allTypes: availableFilterIds.length ? includedFilters.length === availableFilterIds.length : input?.allTypes,
        ...invertedCustomOptions,
    };
};

const normalizePatientLabelsForDb = (input = {}, allPatientLabelsList = []) => {
    const selectedLabels = input?.patientLabels || [];
    const availableLabelIds = (allPatientLabelsList || []).map((label) => label.id).filter(Boolean);

    if (!input?.excludePatientLabels || !availableLabelIds.length) {
        return {
            patientLabels: selectedLabels,
        };
    }

    const selectedSet = new Set(selectedLabels);
    const includedLabels = availableLabelIds.filter((id) => !selectedSet.has(id));

    return {
        patientLabels: includedLabels,
    };
};

export const updateWorkQueueFilters = (
    filters,
    allBranchesList = [],
    allCategoriesList = [],
    allActivityTypesList = [],
    allPatientLabelsList = [],
    allCustomOptionsList = []
) => {
    const normalizedBranchFilters = normalizeBranchesForDb(filters, allBranchesList);
    const normalizedCategoryFilters = normalizeCategoriesForDb(filters, allCategoriesList);
    const normalizedActivityTypeFilters = normalizeActivityTypesForDb(
        filters,
        allActivityTypesList,
        allCustomOptionsList
    );
    const normalizedPatientLabels = normalizePatientLabelsForDb(filters, allPatientLabelsList);

    return R.compose(
        R.over(R.lensProp('dateStart'), workQueueFormatDate),
        R.over(R.lensProp('dateEnd'), workQueueFormatDate),
        R.over(R.lensProp('search'), convertSearchFilter)
    )({
        ...filters,
        ...normalizedBranchFilters,
        ...normalizedCategoryFilters,
        ...normalizedActivityTypeFilters,
        ...normalizedPatientLabels,
    });
};

const transformFilterToReactCommon = (input = {}) => {
    const inputFilters = input?.filter || input;
    return {
        search: {
            humanId: inputFilters?.filter_human_id || undefined,
            externalId: inputFilters?.filter_external_id || undefined,
            ehrId: inputFilters?.filter_ehr_id || undefined,
            otherId: inputFilters?.filter_other_id || undefined,
            patientName: inputFilters?.filter_patient_name || undefined,
            medication: inputFilters?.filter_medication || undefined,
            medicationAssociation: inputFilters?.filter_medication_association || undefined,
            dateDueSearch: inputFilters?.filter_date_due_search || undefined,
            activityName: inputFilters?.filter_activity_name || undefined,
            patientDob: inputFilters?.filter_patient_dob || undefined,
            activityStatus: inputFilters?.filter_activity_status || undefined,
            activityStatusDate: inputFilters?.filter_activity_status_date || undefined,
            updatedName: inputFilters?.filter_updated_name || undefined,
            assignedTo: inputFilters?.filter_assigned_to || undefined,
            patientLabels: inputFilters?.filter_patient_labels || undefined,
            needsBySearch: inputFilters?.filter_needs_by_search || undefined,
            branchName: inputFilters?.filter_branch_name || undefined,
            patientState: inputFilters?.filter_patient_state || undefined,
            patientStatus: inputFilters?.filter_patient_status || undefined,
            insurancePlanName: inputFilters?.filter_insurance_plan_name || undefined,
        },
        dateEnd: toDate(inputFilters?.dateEnd),
        dateStart: toDate(inputFilters?.dateStart),
        type: inputFilters?.dateRange || 'this_week',
        branches: Object.keys(inputFilters?.branches || {}),
        highRisk: inputFilters?.highRisk,
        onlyMine: inputFilters?.onlyMine,
        allTypes: inputFilters?.all_types,
        categories: Object.keys(inputFilters?.group_ids || {}),
        patientLabels: inputFilters?.patientLabels || [],
        allBranches: inputFilters?.all_branches,
        assignedToMe: inputFilters?.assignedToMe,
        filters: Object.keys(inputFilters?.activityTypes || {}),
        predictiveModelRisk: inputFilters?.predictiveModelRisk,
        allCategories: inputFilters?.all_categories,
        excludeBranches: input?.excludeBranches || false,
        excludeCategories: input?.excludeCategories || false,
        excludeActivityTypes: input?.excludeActivityTypes || false,
        excludePatientLabels: input?.excludePatientLabels || false,
    };
};

export const transformDbFilterToReactState = (
    input,
    allBranchesList = [],
    allCategoriesList = [],
    allActivityTypesList = [],
    allPatientLabelsList = [],
    allCustomOptionsList = []
) => {
    const reactState = transformFilterToReactCommon(input);

    // Saved filters persist normalized include-lists together with the `exclude*` flags.
    // Re-running the loaded state through the same normalize functions reverses that
    // inversion (the complement is an involution), restoring the original excluded
    // selection. Otherwise `updateWorkQueueFilters` would invert a second time at query
    // time and produce an incorrect backend query (double inversion).
    const normalizedBranchFilters = normalizeBranchesForDb(reactState, allBranchesList);
    const normalizedCategoryFilters = normalizeCategoriesForDb(reactState, allCategoriesList);
    const normalizedActivityTypeFilters = normalizeActivityTypesForDb(
        reactState,
        allActivityTypesList,
        allCustomOptionsList
    );
    const normalizedPatientLabels = normalizePatientLabelsForDb(reactState, allPatientLabelsList);

    return {
        ...reactState,
        ...normalizedBranchFilters,
        ...normalizedCategoryFilters,
        ...normalizedActivityTypeFilters,
        ...normalizedPatientLabels,
        page: 1,
        count: input?.count || 25,
        sorting: input?.sorting || {},
    };
};

export const transformLsFilterToReact = (input = {}) => {
    return {
        ...transformFilterToReactCommon(input),
        page: input?.page || 1,
        count: input?.count || 25,
        sorting: input?.sorting || {},
        mobileAnswersFilterParams: input?.mobileAnswersFilterParams || {
            search: {},
            page: 1,
            count: 10,
            sorting: {
                patient_name: 'asc',
            },
        },
    };
};

const transformFilterToSaveCommon = (input) => {
    return {
        activityTypes: input.filters.reduce((acc, filter) => {
            acc[filter] = true;
            return acc;
        }, {}),
        all_branches: input.allBranches,
        all_categories: input.allCategories,
        all_types: input.allTypes,
        assignedToMe: input.assignedToMe,
        branches: input.branches.reduce((acc, branch) => {
            acc[branch] = true;
            return acc;
        }, {}),
        dateRange: input.type,
        dateEnd: input.dateEnd,
        dateStart: input.dateStart,
        filter_activity_name: input?.search?.activityName || undefined,
        filter_activity_status: input?.search?.activityStatus || undefined,
        filter_activity_status_date: input?.search?.activityStatusDate || undefined,
        filter_assigned_to: input?.search?.assignedTo || undefined,
        filter_branch_name: input?.search?.branchName || undefined,
        filter_patient_state: input?.search?.patientState || undefined,
        filter_patient_status: input?.search?.patientStatus || undefined,
        filter_insurance_plan_name: input?.search?.insurancePlanName || undefined,
        filter_date_due_search: input?.search?.dateDueSearch || undefined,
        filter_human_id: input?.search?.humanId || undefined,
        filter_external_id: input?.search?.externalId || undefined,
        filter_ehr_id: input?.search?.ehrId || undefined,
        filter_other_id: input?.search?.otherId || undefined,
        filter_medication: input?.search?.medication || undefined,
        filter_medication_association: input?.search?.medicationAssociation || undefined,
        filter_needs_by_search: input?.search?.needsBySearch || undefined,
        filter_patient_dob: input?.search?.patientDob || undefined,
        filter_patient_labels: input?.search?.patientLabels || undefined,
        filter_patient_name: input.search.patientName || undefined,
        filter_updated_name: input?.search?.updatedName || undefined,
        group_ids: input.categories.reduce((acc, category) => {
            acc[category] = true;
            return acc;
        }, {}),
        patientLabels: input?.patientLabels,
        highRisk: input.highRisk,
        onlyMine: input.onlyMine,
        search: '',
        predictiveModelRisk: input?.search?.predictiveModelRisk || input?.predictiveModelRisk || false,
        excludeBranches: input?.excludeBranches || false,
        excludeCategories: input?.excludeCategories || false,
        excludeActivityTypes: input?.excludeActivityTypes || false,
        excludePatientLabels: input?.excludePatientLabels || false,
    };
};

export const transformReactFilterToSave = (
    input,
    allBranchesList = [],
    allCategoriesList = [],
    allActivityTypesList = [],
    allPatientLabelsList = [],
    allCustomOptionsList = []
) => {
    const normalizedBranchFilters = normalizeBranchesForDb(input, allBranchesList);
    const normalizedCategoryFilters = normalizeCategoriesForDb(input, allCategoriesList);
    const normalizedActivityTypeFilters = normalizeActivityTypesForDb(
        input,
        allActivityTypesList,
        allCustomOptionsList
    );
    const normalizedPatientLabels = normalizePatientLabelsForDb(input, allPatientLabelsList);
    const output = {
        ...transformFilterToSaveCommon({
            ...input,
            ...normalizedBranchFilters,
            ...normalizedCategoryFilters,
            ...normalizedActivityTypeFilters,
            ...normalizedPatientLabels,
        }),
        count: input?.count ?? 25,
        sorting: input?.sorting ?? {},
    };

    return JSON.stringify(output);
};

export const transformReactFilterToLS = (input) => {
    return {
        page: input?.filterParams?.page ?? 1,
        count: input?.filterParams?.count ?? 25,
        filter: {
            ...transformFilterToSaveCommon(input?.filterParams),
        },
        pendingFilterParams: input?.pendingFilterParams || {},
        sorting: input?.filterParams?.sorting ?? {},
        mobileAnswersFilterParams: input?.mobileAnswersFilterParams || {
            search: {},
            page: 1,
            count: 10,
            sorting: {
                patient_name: 'asc',
            },
        },
        excludeBranches: input?.filterParams?.excludeBranches || false,
        excludeCategories: input?.filterParams?.excludeCategories || false,
        excludeActivityTypes: input?.filterParams?.excludeActivityTypes || false,
        excludePatientLabels: input?.filterParams?.excludePatientLabels || false,
        group: {},
    };
};
